import React, { useRef, useEffect, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import { MAPBOX_KEY, MAPBOX_BACKGROUND } from "../../utils/map";
import "mapbox-gl/dist/mapbox-gl.css";
import "./CompareMap.css";

mapboxgl.accessToken = MAPBOX_KEY;

const SOURCE_ID  = "compare-stations";
const LAYER_ID   = "compare-dots";
const LAYER_HL   = "compare-dots-highlight";

const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] };

const CompareMap = ({ geojson, onStationClick, selectedWigosid }) => {
  const containerRef      = useRef(null);
  const mapRef            = useRef(null);
  const popupRef          = useRef(new mapboxgl.Popup({ closeButton: false, closeOnClick: true, maxWidth: "none" }));
  const onStationClickRef = useRef(onStationClick);

  useEffect(() => {
    onStationClickRef.current = onStationClick;
  }, [onStationClick]);

  const initMap = useCallback(() => { // stable — callbacks accessed via ref
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAPBOX_BACKGROUND || "mapbox://styles/mapbox/dark-v11",
      center: [0, 20],
      zoom: 2,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    map.addControl(new mapboxgl.ScaleControl({ unit: "metric" }), "bottom-left");

    map.on("load", () => {
      map.addSource(SOURCE_ID, { type: "geojson", data: EMPTY_GEOJSON });

      map.addLayer({
        id:     LAYER_ID,
        type:   "circle",
        source: SOURCE_ID,
        paint: {
          "circle-color":        ["coalesce", ["get", "diff_color"], "#999999"],
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            1, 2.5,
            6, 4,
            10, 7,
          ],
          "circle-stroke-width": 1,
          "circle-stroke-color": "rgba(255,255,255,0.5)",
          "circle-opacity":      0.88,
        },
      });

      map.addLayer({
        id:     LAYER_HL,
        type:   "circle",
        source: SOURCE_ID,
        filter: ["==", ["get", "wigosid"], ""],
        paint: {
          "circle-color":        ["coalesce", ["get", "diff_color"], "#999999"],
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            1, 4,
            6, 6,
            10, 10,
          ],
          "circle-stroke-width": 2.5,
          "circle-stroke-color": "#ffffff",
          "circle-opacity":      1,
        },
      });

      map.on("click", LAYER_ID, (e) => {
        if (!e.features.length) return;
        const props = e.features[0].properties;
        const coords = e.features[0].geometry.coordinates.slice();
        if (onStationClickRef.current) onStationClickRef.current(props, coords);
        popupRef.current
          .setLngLat(coords)
          .setHTML(`<div class="cmap-popup-loading">Loading…</div>`)
          .addTo(map);
      });

      map.on("mouseenter", LAYER_ID, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", LAYER_ID, () => {
        map.getCanvas().style.cursor = "";
      });
    });

    mapRef.current = map;
    return map;
  }, []); // stable — callbacks accessed via ref

  useEffect(() => {
    const map = initMap();
    return () => map.remove();
  }, [initMap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const applyData = () => {
      const source = map.getSource(SOURCE_ID);
      if (source) source.setData(geojson || EMPTY_GEOJSON);
    };

    // If source already exists (map fully loaded), update immediately.
    // Otherwise queue the update for after the load event fires.
    // NOTE: isStyleLoaded() can return false when the base style has
    // validation errors (e.g. missing vector tile layers in the ECMWF
    // custom style), so we use getSource() as the readiness gate instead.
    if (map.getSource(SOURCE_ID)) {
      applyData();
    } else {
      const onLoad = () => applyData();
      map.once("load", onLoad);
      return () => map.off("load", onLoad);
    }
  }, [geojson]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer(LAYER_HL)) return;
    map.setFilter(LAYER_HL, [
      "==", ["get", "wigosid"], selectedWigosid || "",
    ]);
  }, [selectedWigosid]);

  const updatePopupContent = useCallback((html) => {
    if (popupRef.current.isOpen()) {
      const el = popupRef.current.getElement();
      if (el) el.querySelector(".mapboxgl-popup-content").innerHTML = html;
    }
  }, []);

  CompareMap.updatePopup = updatePopupContent;
  CompareMap.closePopup  = () => {
    if (popupRef.current) popupRef.current.remove();
  };
  CompareMap.openPopup   = useCallback((lngLat, html) => {
    if (!mapRef.current) return;
    popupRef.current
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(mapRef.current);
    mapRef.current.easeTo({ center: lngLat, duration: 400 });
  }, []);

  return <div ref={containerRef} className="cmap-container" />;
};

export default CompareMap;
